import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { GfRegister } from "@/pages/company/GfRegister";
import { MeetingFinancials } from "@/pages/company/MeetingFinancials";
import { MeetingHeader } from "@/pages/company/meeting/MeetingHeader";
import { AgendaTab } from "@/pages/company/meeting/AgendaTab";
import { MeetingProtocol } from "@/pages/company/meeting/MeetingProtocol";
import { MeetingSignatures } from "@/pages/company/meeting/MeetingSignatures";
import { MeetingVideo } from "@/pages/company/meeting/MeetingVideo";
import { MeetingAttachments } from "@/pages/company/meeting/MeetingAttachments";

const CHAIR_ROLES = ["Styreleder", "Nestleder", "Daglig leder"];

export default function MeetingDetail() {
  const { companyId, meetingId } = useParams();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [gf, setGf] = useState(null);
  const [companyTasks, setCompanyTasks] = useState([]);
  const [live, setLive] = useState(null);

  const loadGf = useCallback(() => api.get(`/meetings/${meetingId}/gf`).then((r) => setGf(r.data)).catch(() => {}), [meetingId]);
  const loadTasks = useCallback(() => api.get(`/companies/${companyId}/tasks`).then((r) => setCompanyTasks(r.data)).catch(() => {}), [companyId]);
  const refreshLive = useCallback(() => api.get(`/meetings/${meetingId}/live`).then((r) => setLive(r.data)).catch(() => {}), [meetingId]);

  const load = useCallback(() =>
    api.get(`/meetings/${meetingId}`).then((r) => {
      setM(r.data);
      if (r.data.type === "generalforsamling" || r.data.type === "ekstraordinar_gf") loadGf();
    }), [meetingId, loadGf]);

  useEffect(() => {
    setM(null);
    setGf(null);
    setLive(null);
    load();
    loadTasks();
    refreshLive();
  }, [meetingId, load, loadTasks, refreshLive]);

  useEffect(() => {
    const id = setInterval(() => refreshLive(), 2500);
    return () => clearInterval(id);
  }, [refreshLive]);

  if (!m) {
    return (
      <div className="grid h-64 place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const isGf = m.type === "generalforsamling" || m.type === "ekstraordinar_gf";
  const videoActive = !!live?.video?.active;
  const myAtt = m.attendees?.find((a) => a.user_id === user?.id);
  const canManage = !!user?.is_admin || CHAIR_ROLES.includes(myAtt?.role);

  return (
    <div className="mx-auto max-w-7xl">
      <MeetingHeader m={m} meetingId={meetingId} companyId={companyId} load={load} canManage={canManage} />

      <Tabs defaultValue="agenda" className="mt-8">
        <TabsList>
          <TabsTrigger value="agenda" data-testid="tab-agenda">Sakliste</TabsTrigger>
          {isGf ? <TabsTrigger value="gf" data-testid="tab-gf">Aksjonærer &amp; fullmakter</TabsTrigger> : null}
          {isGf ? <TabsTrigger value="vedlegg" data-testid="tab-vedlegg">Vedlegg</TabsTrigger> : null}
          {m.financials?.enabled ? <TabsTrigger value="nokkeltall" data-testid="tab-nokkeltall">Nøkkeltall</TabsTrigger> : null}
          <TabsTrigger value="video" data-testid="tab-video">
            <span className="flex items-center gap-1.5">
              Videomøte
              {videoActive ? <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" data-testid="video-active-dot" /> : null}
            </span>
          </TabsTrigger>
          <TabsTrigger value="protokoll" data-testid="tab-protokoll">Protokoll &amp; AI</TabsTrigger>
          <TabsTrigger value="deltakere" data-testid="tab-deltakere">Deltakere &amp; signering</TabsTrigger>
        </TabsList>

        <TabsContent value="agenda" className="mt-6">
          <AgendaTab m={m} meetingId={meetingId} companyId={companyId} isGf={isGf} gf={gf} load={load} companyTasks={companyTasks} loadTasks={loadTasks} live={live} refreshLive={refreshLive} />
        </TabsContent>

        {isGf ? (
          <TabsContent value="gf" className="mt-6">
            <GfRegister meetingId={meetingId} m={m} gf={gf} onChange={load} />
          </TabsContent>
        ) : null}

        {isGf ? (
          <TabsContent value="vedlegg" className="mt-6">
            <MeetingAttachments meetingId={meetingId} canManage={canManage} />
          </TabsContent>
        ) : null}

        {m.financials?.enabled ? (
          <TabsContent value="nokkeltall" className="mt-6">
            <MeetingFinancials meetingId={m.id} />
          </TabsContent>
        ) : null}

        <TabsContent value="video" className="mt-6">
          <MeetingVideo meetingId={meetingId} live={live} refreshLive={refreshLive} />
        </TabsContent>

        <TabsContent value="protokoll" className="mt-6 space-y-6">
          <MeetingProtocol m={m} meetingId={meetingId} setM={setM} load={load} />
        </TabsContent>

        <TabsContent value="deltakere" className="mt-6 grid gap-6 md:grid-cols-2">
          <MeetingSignatures m={m} meetingId={meetingId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
